import { Button } from 'components/button/Button';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

import { OrderStatus } from 'src/types/enums';
import { confirmOrderUsingToken, useOrdersByToken } from '../../models';
import type { OrderStatusValue } from '../../endpoints/shared.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { parseURLSearchParams } from './utils';
import { SimpleErrorBoundary } from '../../../../components/SimpleErrorBoundary';
import { PageHeader } from '../../../../components/PageHeader';
import { SpinnerBlock } from '../../../../components/Spinner';
import { nullthrows } from '../../../../utils/invariant';
import { OrderSummary } from './OrderSummary';
import { CarrierTermsCheckbox } from '../../components/CarrierTermsCheckbox';
import { useTranslation } from '../../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

export interface Props {
  parsed: ReturnType<typeof parseURLSearchParams>;
}

const OrderConfirmationPageContent: React.FC<Props> = (props) => {
  const { parsed } = props;
  const tokenId = parsed.orderId ?? parsed.tokenId ?? '';
  const { data: orderData, isLoading } = useOrdersByToken(tokenId, parsed.token);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const { i18n } = useTranslation();

  if (isLoading) {
    return <SpinnerBlock message="Laden..." />;
  }

  const orders = nullthrows(orderData?.orders, 'Orders not found');
  const termsRequired = orderData?.carrierTermsRequired ?? false;

  const planningDate = parsed.planningDate ?? '';

  if (!orders.length) {
    throw new Error('Orders not found');
  }

  // Gated on the terms checkbox above the button whenever acceptance is required:
  // confirming then always carries the carrier's explicit acceptance of the
  // general terms & conditions.
  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await confirmOrderUsingToken({
        id: tokenId,
        token: parsed.token,
        isConfirm: true,
        termsAccepted: true,
      });
      trackEvent('order_confirmed', {
        token_id: tokenId,
        order_count: orders.length,
        is_planning: Boolean(planningDate),
      });
      setIsConfirmed(true);
      toast.success(
        i18n(planningDate ? 'pages.confirmPlanning.planningConfirmed' : 'pages.confirmOrder.orderConfirmed'),
      );
    } catch (err) {
      captureException(err);
      console.error(err);
      toast.error(i18n('pages.confirmOrder.orderConfirmationError', { error: getDisplayError(err) }));
    } finally {
      setIsConfirming(false);
    }
  };

  const orderNumbers = orders.map((order) => order.orderNumber);
  const generalHeading = planningDate
    ? i18n('pages.confirmPlanning.heading', {
        planningDate,
      })
    : i18n('pages.confirmOrder.heading', {
        orderNumber: orderNumbers.join(','),
      });

  let existingStatus: OrderStatusValue = OrderStatus.Cancelled;
  for (const order of orders) {
    if (order.status !== existingStatus) {
      if (existingStatus !== OrderStatus.Placed && order.status !== OrderStatus.Cancelled) {
        existingStatus = order.status;
      }
    }
  }

  let headingText = '';
  if (existingStatus === OrderStatus.Refused) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingRefused' : 'pages.confirmOrderStates.headingRefused',
    );
  } else if (existingStatus === OrderStatus.Cancelled) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingCancelled' : 'pages.confirmOrderStates.headingCancelled',
    );
  } else if (existingStatus === OrderStatus.Confirmed || isConfirmed) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingConfirmed' : 'pages.confirmOrderStates.headingConfirmed',
    );
  } else if (existingStatus === OrderStatus.Placed && !isConfirmed) {
    headingText = generalHeading;
  } else {
    headingText = i18n(
      planningDate
        ? 'pages.confirmOrderPlanningStates.headingUnknownStatus'
        : 'pages.confirmOrderStates.headingUnknownStatus',
    );
  }

  return (
    <div>
      <PageHeader title={generalHeading} />

      <h1 className="heading-one mb-4">{headingText}</h1>

      {orders.map((order) => {
        return <OrderSummary key={order.id} order={order} />;
      })}

      {existingStatus === OrderStatus.Placed && !isConfirmed && (
        <div className="grid gap-4 justify-items-start">
          <CarrierTermsCheckbox
            isRequired={termsRequired}
            isChecked={hasAcceptedTerms}
            onChange={setHasAcceptedTerms}
            isDisabled={isConfirming}
          />

          <Button
            color="primary"
            onTrigger={handleConfirm}
            isDisabled={isConfirming || (termsRequired && !hasAcceptedTerms)}
            isLoading={isConfirming}
          >
            {i18n(planningDate ? 'pages.confirmPlanning.confirmButton' : 'pages.confirmOrder.confirmButton')}
          </Button>
        </div>
      )}
    </div>
  );
};

const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const parsed = parseURLSearchParams(searchParams);

  return (
    <div>
      <div className="w-full mx-auto p-12 max-w-3xl">
        <SimpleErrorBoundary>
          <OrderConfirmationPageContent parsed={parsed} />
        </SimpleErrorBoundary>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
